import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { memoryApi } from "../services/api";
import { extractList } from "./utils";

/**
 * Fetch hook for the review queue (due memories, optionally per deck).
 *
 * Distinguishes the first load (`loading` — full-page spinner) from
 * deck-switch re-fetches (`refreshing` — dim-the-cards overlay) so the
 * component doesn't flash the whole page on every filter change.
 *
 * The consumer copies `queue` into its own state and pops cards off as
 * they're reviewed; the hook only delivers what the server returned.
 */
const useReviewQueue = (deckId, enabled = true) => {
  const [queue, setQueue] = useState([]);
  // Total due cards (server count), which can exceed what one session returns —
  // the review action caps `results` at REVIEW_SESSION_CAP. The consumer
  // compares totalDue to the loaded queue to surface "N more due".
  const [totalDue, setTotalDue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  // Bumped by reload() to fetch the next batch after a session is cleared.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const fetchQueue = async () => {
      if (hasFetchedRef.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await memoryApi.getReview(deckId, {
          signal: controller.signal,
        });
        const results = extractList(response.data);
        setQueue(results);
        setTotalDue(response.data?.count ?? results.length);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError(err);
        }
      } finally {
        // Skip state updates if aborted (a newer fetch owns the lifecycle)
        if (!controller.signal.aborted) {
          hasFetchedRef.current = true;
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    fetchQueue();
    return () => controller.abort();
  }, [deckId, enabled, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return { queue, totalDue, loading, refreshing, error, reload };
};

export default useReviewQueue;
